import { useMemo, useState } from 'react'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import type { ColumnDef } from '@tanstack/react-table'
import { MicIcon, MoreHorizontalIcon, PlusIcon } from 'lucide-react'

import { Button } from '@ems/ui/components/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@ems/ui/components/dropdown-menu'
import { ConfirmDialog } from '@ems/ui/patterns/confirm-dialog'
import { DataTable } from '@ems/ui/patterns/data-table'
import { EmptyState } from '@ems/ui/patterns/empty-state'
import { PageHeader } from '@ems/ui/patterns/page-header'
import { StatusBadge } from '@ems/ui/patterns/status-badge'

import { ReadOnlyNotice } from '#admin/components/read-only-notice.tsx'
import { sessionQuery } from '#admin/features/auth/queries.ts'
import { can } from '#admin/features/auth/session.ts'
import type { ListedSpeaker, Speaker } from '#admin/features/program/api.ts'
import { IssuePassDialog } from '#admin/features/program/issue-pass-dialog.tsx'
import { useDeleteSpeaker, useSetSpeakerPublished } from '#admin/features/program/mutations.ts'
import { sessionsQuery, speakersQuery } from '#admin/features/program/queries.ts'
import { SpeakerDialog } from '#admin/features/program/speaker-dialog.tsx'
import { sessionCounts } from '#admin/features/program/speakers.ts'
import { moduleAccess } from '#admin/features/tenant/api.ts'
import { manifestQuery } from '#admin/features/tenant/queries.ts'
import { SpeakersSkeleton } from '#admin/features/program/program-skeletons.tsx'
import {
  matchesSpeaker,
  SPEAKER_FILTER_LABEL,
  SPEAKER_FILTERS,
  type SpeakerFilter,
} from '#admin/features/program/list-filters.ts'
import { FilteredEmpty, ListFilterBar } from '#admin/components/list-filter.tsx'
import { pageSlice, TablePagination, usePagination } from '#admin/components/table-pagination.tsx'

/**
 * The people on the program, drafts included, and the editor for them. Gated on `rundown`.
 *
 * **Not the website's speakers.** The CMS keeps its own marketing `speaker` collection, separate
 * on purpose (`packages/cms/src/collections.ts`); the description says so, so nobody edits the
 * wrong one.
 */
export const Route = createFileRoute('/_authenticated/program/speakers')({
  pendingComponent: SpeakersSkeleton,
  staticData: { crumb: { parents: [{ label: 'Program' }], label: 'Speakers' } },
  loader: ({ context }) =>
    Promise.all([
      context.queryClient.ensureQueryData(speakersQuery()),
      context.queryClient.ensureQueryData(sessionsQuery()),
      context.queryClient.ensureQueryData(sessionQuery()),
      context.queryClient.ensureQueryData(manifestQuery()),
    ]),
  component: SpeakersPage,
})

function SpeakersPage() {
  const { data: speakers } = useSuspenseQuery(speakersQuery())
  const { data: sessions } = useSuspenseQuery(sessionsQuery())
  const { data: session } = useSuspenseQuery(sessionQuery())
  const { data: manifest } = useSuspenseQuery(manifestQuery())
  const canManage = can(session, 'rundown.manage')
  // The account lookup is `ticket`'s, and needs `ticket.manage`.
  const canLink =
    moduleAccess(manifest ?? undefined, 'ticket') !== 'none' && can(session, 'ticket.manage')
  const canUpload = can(session, 'identity.write')

  const [editing, setEditing] = useState<{ speaker: Speaker | null } | null>(null)
  const [deleting, setDeleting] = useState<Speaker | null>(null)
  const [issuing, setIssuing] = useState<Speaker | null>(null)
  // Filtered in the browser: the list is already here in full.
  const [query, setQuery] = useState('')
  const [filter, setFilter] = useState<SpeakerFilter>('all')
  const shown = speakers.items.filter((speaker) => matchesSpeaker(speaker, query, filter))
  // One page at a time. A new search or pill starts again at the first page.
  const { page, setPage, pageSize, setPageSize } = usePagination(
    `${filter}|${query.trim().toLowerCase()}`,
  )
  const paged = pageSlice(shown, page, pageSize)
  const { mutate: setPublished, isPending: publishing } = useSetSpeakerPublished()
  const remove = useDeleteSpeaker(() => setDeleting(null))

  const published = speakers.items.filter((s) => s.isPublished).length
  // Null on every row when ticketing is off: no pass is asked for, so no column to show.
  const passesAsked = speakers.items.some((s) => s.hasPass !== null)

  const columns = useMemo<ColumnDef<ListedSpeaker, never>[]>(() => {
    const counts = sessionCounts(sessions.items)
    const muted = (value: string | null) => (
      <span className="text-muted-foreground">{value ?? '—'}</span>
    )
    return [
      { accessorKey: 'name', header: 'Speaker', meta: { primary: true } },
      { id: 'headline', header: 'Headline', cell: ({ row }) => muted(row.original.headline) },
      { id: 'company', header: 'Company', cell: ({ row }) => muted(row.original.company) },
      {
        id: 'account',
        header: 'Account',
        cell: ({ row }) => muted(row.original.personId === null ? 'No' : 'Linked'),
      },
      ...(passesAsked
        ? [
            {
              id: 'pass',
              header: 'Speaker pass',
              cell: ({ row }: { row: { original: ListedSpeaker } }) => (
                <StatusBadge tone={row.original.hasPass === true ? 'success' : 'warning'}>
                  {row.original.hasPass === true ? 'Held' : 'None'}
                </StatusBadge>
              ),
            } satisfies ColumnDef<ListedSpeaker, never>,
          ]
        : []),
      {
        id: 'sessions',
        header: 'Sessions',
        meta: { align: 'end' },
        cell: ({ row }) => counts.get(row.original.id) ?? 0,
      },
      {
        id: 'published',
        header: 'State',
        cell: ({ row }) => (
          <StatusBadge tone={row.original.isPublished ? 'success' : 'neutral'}>
            {row.original.isPublished ? 'Published' : 'Unpublished'}
          </StatusBadge>
        ),
      },
      ...(canManage
        ? [
            {
              id: 'actions',
              header: () => <span className="sr-only">Actions</span>,
              meta: { align: 'end' as const },
              cell: ({ row }: { row: { original: ListedSpeaker } }) => (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      aria-label={`Actions for ${row.original.name}`}
                    >
                      <MoreHorizontalIcon />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onSelect={() => setEditing({ speaker: row.original })}>
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuItem
                      disabled={publishing}
                      onSelect={() =>
                        setPublished({
                          speaker: row.original,
                          isPublished: !row.original.isPublished,
                        })
                      }
                    >
                      {row.original.isPublished ? 'Unpublish' : 'Publish'}
                    </DropdownMenuItem>
                    {row.original.hasPass === false ? (
                      <DropdownMenuItem onSelect={() => setIssuing(row.original)}>
                        Issue speaker pass
                      </DropdownMenuItem>
                    ) : null}
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      variant="destructive"
                      onSelect={() => setDeleting(row.original)}
                    >
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              ),
            } satisfies ColumnDef<ListedSpeaker, never>,
          ]
        : []),
    ]
  }, [canManage, passesAsked, sessions.items, setPublished, publishing])

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title="Speakers"
        description={`${published} of ${speakers.items.length} speaker${
          speakers.items.length === 1 ? '' : 's'
        } published`}
        actions={
          canManage ? (
            <Button onClick={() => setEditing({ speaker: null })}>
              <PlusIcon />
              Add speaker
            </Button>
          ) : null
        }
      />

      {canManage ? null : <ReadOnlyNotice area="the speakers" />}

      {speakers.items.length === 0 ? null : (
        <ListFilterBar
          query={query}
          onQueryChange={setQuery}
          placeholder="Search by speaker name, company, or headline..."
          inputClassName="sm:max-w-xs"
          status={filter}
          onStatusChange={setFilter}
          statusLabel="Publication"
          options={SPEAKER_FILTERS.map((value) => ({
            value,
            label: SPEAKER_FILTER_LABEL[value],
            count: speakers.items.filter((speaker) => matchesSpeaker(speaker, '', value)).length,
          }))}
        />
      )}

      <DataTable
        caption="Program speakers"
        title="On the program"
        description="The people placed on sessions. The speakers on the website’s pages are a separate list, edited under Content."
        // Only with rows: an empty table shows its empty state, and no line under it.
        footer={
          shown.length === 0 ? undefined : (
            <TablePagination
              page={paged.page}
              pageSize={pageSize}
              onPageSizeChange={setPageSize}
              totalItems={shown.length}
              onPageChange={setPage}
              ariaLabel="Pages of the speakers"
            />
          )
        }
        columns={columns}
        data={paged.rows}
        getRowId={(s) => s.id}
        empty={
          speakers.items.length > 0 ? (
            <FilteredEmpty
              message="No speakers matching your search"
              clearLabel="Clear search"
              onClear={() => {
                setQuery('')
                setFilter('all')
              }}
            />
          ) : (
            <EmptyState
              icon={<MicIcon />}
              title="No speakers yet"
              description="Add a speaker, then put them on sessions from the session dialog."
            />
          )
        }
      />

      {editing === null ? null : (
        <SpeakerDialog
          // Keyed, so opening another speaker starts from their own values rather than the last edit.
          key={editing.speaker?.id ?? 'new'}
          speaker={editing.speaker}
          canLink={canLink}
          canUpload={canUpload}
          onClose={() => setEditing(null)}
        />
      )}

      {issuing === null ? null : (
        <IssuePassDialog key={issuing.id} speaker={issuing} onClose={() => setIssuing(null)} />
      )}

      <ConfirmDialog
        open={deleting !== null}
        onOpenChange={(open) => (open ? undefined : setDeleting(null))}
        title={`Delete ${deleting?.name ?? ''}?`}
        description="They come off every session and the public program. A speaker with meetings booked can’t be deleted; cancel those first."
        confirmLabel="Delete"
        destructive
        busy={remove.isPending}
        onConfirm={() => (deleting === null ? undefined : remove.mutate(deleting))}
      />
    </div>
  )
}
